import {css, Icon, Link, view} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  LightCard,
  ThemeProvider,
  LinkTheme,
  StatusBadge,
  StatusType,
  LightThemeButton,
  useNewUniversalHelpDesign,
  useTheme,
} from '@stripe-internal/conversations-ui';
import {safeWindowOpen} from '@stripe-internal/safe-links';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {business} from '@sail/icons/react/Icon';
import {
  LinkTransactionCardViewBlockchainAnalytics,
  LinkTransactionCardViewTransactionAnalytics,
} from './analytics';

export type ValueProps = {
  merchant_name: string;
  merchant_avatar: string;
  purchase_amount: string;
  purchase_date: number;
  transaction_status: StatusType;
  blockchain_tx_id: string;
  transaction_link: string;
  link_brand_name: string;
};

const messages = defineMessages({
  purchaseDate: {
    id: 'mse_inline_data.universal_help_transaction_detail.purchased_on',
    description: 'Message displaying the date of a purchase.',
    defaultMessage: 'Post date',
  },
  purchaseAmount: {
    id: 'mse_inline_data.universal_help_transaction_detail.purchased_amount',
    description: 'Message displaying the amount of a purchase.',
    defaultMessage: 'Amount',
  },
  blockChainId: {
    id: 'mse_inline_data.universal_help_transaction_detail.blockchainid',
    description: 'Heading for block chain id',
    defaultMessage: 'Blockchain ID',
  },
  transactionStatus: {
    id: 'mse_inline_data.universal_help_transaction_detail.transactionStatus',
    description: 'Heading for Transaction status',
    defaultMessage: 'Transaction status',
  },
  viewInLinkApp: {
    id: 'mse_inline_data.universal_help_transaction_detail.viewInLinkApp',
    description: 'Message directing user to Link Dashboard.',
    defaultMessage: 'View in {linkBrandName} App',
  },
});

const LinkTransactionCardDetail = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const intl = useIntl();
  const analytics = useAnalytics();
  const theme = useTheme();
  const {
    merchant_name: merchantName,
    purchase_amount: purchaseAmount,
    purchase_date: purchaseDate,
    merchant_avatar: merchantAvatar,
    transaction_status: transactionStatus,
    blockchain_tx_id: blockchainTxId,
    transaction_link: transactionLink,
    link_brand_name: linkBrandName,
  } = value;

  const handleClick = () => {
    analytics.action(LinkTransactionCardViewTransactionAnalytics);
    safeWindowOpen(transactionLink);
  };
  const isBlockchain = blockchainTxId.length > 0;
  const blockchainLink = isBlockchain
    ? `https://www.blockchain.com/explorer/search?search=${blockchainTxId}`
    : '';
  const isSucceededOrRefunded =
    transactionStatus === 'succeeded' || transactionStatus === 'refunded';

  const localizedPurchaseDate =
    purchaseDate &&
    intl.formatDate(new Date(purchaseDate * 1000), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // TODO (saimuppa, 2025-11-30) remove all conditional stylings once new design is rolled out for Link
  const useNewDesign = useNewUniversalHelpDesign();

  analytics.viewed('viewed_link_transaction_card_detail');
  return (
    <view.div
      css={{
        gap: 'medium',
        stack: 'y',
        color: theme.primaryActionColor,
      }}
    >
      <LightCard isOuter customStyles={{borderRadius: '16px'}}>
        <view.div css={{stack: 'y', gap: 'large'}}>
          <view.div css={{stack: 'x', gap: 'small'}}>
            <view.div
              css={{
                stack: 'x',
                width: 'large',
                height: 'large',
                alignSelfY: 'center',
              }}
            >
              <view.div
                css={{
                  borderRadius: '50%',
                  border: '1px inset rgba(0, 0, 0, 0.15)',
                  width: 'fill',
                  height: 'fill',
                }}
              >
                {merchantAvatar ? (
                  <view.img
                    data-testid="merchant-avatar"
                    src={merchantAvatar}
                    css={{
                      width: 'fill',
                      height: 'fill',
                      borderRadius: '50%',
                      border: '1px inset rgba(0, 0, 0, 0.15)',
                    }}
                  />
                ) : (
                  <Icon
                    icon={business}
                    css={{
                      padding: 'xsmall',
                      width: 'fill',
                      height: 'fill',
                    }}
                  />
                )}
              </view.div>
            </view.div>

            <view.h3
              css={{
                margin: 0,
                font: useNewDesign ? 'heading.medium' : 'heading.large',
              }}
              data-testid="merchant-name"
            >
              {merchantName}
            </view.h3>
          </view.div>
          <view.div>
            <view.div css={{font: 'label.medium.emphasized'}}>
              <FormattedMessage {...messages.purchaseAmount} />
            </view.div>
            <view.div
              data-testid="purchase-amount"
              css={{
                font: useNewDesign ? 'heading.large' : 'heading.xlarge',
                marginBottom: 'medium',
              }}
            >
              {purchaseAmount}
            </view.div>

            <view.div uses={[useNewDesign && css({stack: 'x', gap: 'small'})]}>
              <view.div>
                <view.div css={{font: 'label.medium.emphasized'}}>
                  <FormattedMessage {...messages.purchaseDate} />
                </view.div>
                <view.div
                  data-testid="purchase-date"
                  css={{font: 'label.medium', marginBottom: 'medium'}}
                >
                  {localizedPurchaseDate}
                </view.div>
              </view.div>

              <view.div css={{stack: 'y'}}>
                <view.div css={{font: 'label.medium.emphasized'}}>
                  <FormattedMessage {...messages.transactionStatus} />
                </view.div>

                <view.div
                  data-testid="transaction-status"
                  css={{marginBottom: 'small'}}
                >
                  <StatusBadge status={transactionStatus} />
                </view.div>
              </view.div>
            </view.div>

            {isBlockchain && isSucceededOrRefunded ? (
              <view.div>
                <view.div css={{font: 'label.medium.emphasized'}}>
                  <FormattedMessage {...messages.blockChainId} />
                </view.div>
                <view.div
                  data-testid="blockchain-id"
                  css={{font: 'label.medium'}}
                >
                  <Link
                    type="secondary"
                    href={blockchainLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    uses={[
                      !useNewDesign &&
                        css({
                          color: theme.linkColor,
                          fontWeight: 'bold',
                        }),
                    ]}
                    onPress={() => {
                      analytics.action(
                        LinkTransactionCardViewBlockchainAnalytics,
                      );
                    }}
                  >
                    {blockchainTxId}
                  </Link>
                </view.div>
              </view.div>
            ) : null}
          </view.div>
          {isSucceededOrRefunded ? (
            <LightThemeButton handleClick={handleClick}>
              <FormattedMessage
                {...messages.viewInLinkApp}
                values={{linkBrandName}}
              />
            </LightThemeButton>
          ) : null}
        </view.div>
      </LightCard>
    </view.div>
  );
};

export const LinkTransactionCard = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkTransactionCardDetail props={props} />
    </ThemeProvider>
  );
};
